import type { FormTheme } from '@/types/form'
import type { CSSProperties } from 'react'

export type FormBackgroundType = 'solid' | 'gradient' | 'image' | 'video'
export type FormQuestionAlignment = 'left' | 'center' | 'right'
export type FormVerticalAlign = 'top' | 'center'
export type FormAnswerSpacing = 'compact' | 'comfortable' | 'roomy'
export type FormNavigationLayout = 'inline' | 'split' | 'stacked'
export type FormProgressPosition = 'top' | 'bottom'
export type FormLogoPosition = 'top-left' | 'top-center' | 'top-right'
export type FormLogoShape = 'none' | 'rounded' | 'circle'
export type FormBrandMarkPosition = 'bottom-left' | 'bottom-center' | 'bottom-right'
export type FormBrandMarkStyle = 'subtle' | 'pill' | 'solid'
export type FormBannerDisplay = 'welcome' | 'questions' | 'all'
export type FormBannerFit = 'cover' | 'contain'

export type FormLayoutSettings = {
  questionAlign: FormQuestionAlignment
  verticalAlign: FormVerticalAlign
  answerSpacing: FormAnswerSpacing
  navigationLayout: FormNavigationLayout
  progressPosition: FormProgressPosition
  showQuestionNumber: boolean
  showBackButton: boolean
}

export type FormBrandSettings = {
  showLogo: boolean
  logoUrl: string
  logoAlt: string
  logoText: string
  logoSize: number
  logoPosition: FormLogoPosition
  logoShape: FormLogoShape
  logoFrame: boolean
  showBrandName: boolean
  brandName: string
  logoLinkUrl: string
  faviconUrl: string
  showBrandMark: boolean
  brandMarkText: string
  brandMarkUrl: string
  brandMarkPosition: FormBrandMarkPosition
  brandMarkStyle: FormBrandMarkStyle
  brandMarkOpacity: number
  showBanner: boolean
  bannerUrl: string
  bannerAlt: string
  bannerDisplay: FormBannerDisplay
  bannerHeight: number
  bannerRadius: number
  bannerFit: FormBannerFit
  bannerFocalX: number
  bannerFocalY: number
  bannerZoom: number
  bannerOverlayColor: string
  bannerOverlayOpacity: number
}

type FontScale = NonNullable<FormTheme['fontScale']>

const typographySizes: Record<
  FontScale,
  { question: string; body: string; input: string; meta: string }
> = {
  compact: {
    question: '1.125rem',
    body: '1rem',
    input: '1.0625rem',
    meta: '0.875rem',
  },
  comfortable: {
    question: '1.25rem',
    body: '1.125rem',
    input: '1.25rem',
    meta: '0.9375rem',
  },
  large: {
    question: '1.375rem',
    body: '1.25rem',
    input: '1.375rem',
    meta: '1rem',
  },
}

const answerSpacingValues: Record<
  FormAnswerSpacing,
  { answerGap: string; questionBlockGap: string; pagePaddingBlock: string }
> = {
  compact: {
    answerGap: '0.625rem',
    questionBlockGap: '1.5rem',
    pagePaddingBlock: '3rem',
  },
  comfortable: {
    answerGap: '0.875rem',
    questionBlockGap: '2rem',
    pagePaddingBlock: '4rem',
  },
  roomy: {
    answerGap: '1.25rem',
    questionBlockGap: '2.5rem',
    pagePaddingBlock: '5rem',
  },
}

export function getFormBackgroundType(value: unknown): FormBackgroundType {
  if (value === 'gradient' || value === 'image' || value === 'video') return value
  return 'solid'
}

export function getFormLayoutSettings(settings: Record<string, unknown>): FormLayoutSettings {
  return {
    questionAlign: getQuestionAlignment(settings.questionAlign),
    verticalAlign: settings.verticalAlign === 'top' ? 'top' : 'center',
    answerSpacing: getAnswerSpacing(settings.answerSpacing),
    navigationLayout: getNavigationLayout(settings.navigationLayout),
    progressPosition: settings.progressPosition === 'top' ? 'top' : 'bottom',
    showQuestionNumber: settings.showQuestionNumber !== false,
    showBackButton: settings.showBackButton !== false,
  }
}

export function getFormBrandSettings(settings: Record<string, unknown>): FormBrandSettings {
  const brandName = getString(settings.brandName, 'MiaoForm')

  return {
    showLogo: settings.showLogo === true,
    logoUrl: getString(settings.logoUrl, ''),
    logoAlt: getString(settings.logoAlt, brandName),
    logoText: getString(
      settings.logoText,
      brandName === 'MiaoForm' ? 'MF' : getInitials(brandName),
    ),
    logoSize: clampNumber(settings.logoSize, 32, 160, 32),
    logoPosition: getLogoPosition(settings.logoPosition),
    logoShape: getLogoShape(settings.logoShape),
    logoFrame: settings.logoFrame !== false,
    showBrandName: settings.showBrandName !== false,
    brandName,
    logoLinkUrl: getString(settings.logoLinkUrl, ''),
    faviconUrl: getString(settings.faviconUrl, ''),
    showBrandMark: settings.showBrandMark === true,
    brandMarkText: getString(settings.brandMarkText, 'Powered by MiaoForm'),
    brandMarkUrl: getString(settings.brandMarkUrl, ''),
    brandMarkPosition: getBrandMarkPosition(settings.brandMarkPosition),
    brandMarkStyle: getBrandMarkStyle(settings.brandMarkStyle),
    brandMarkOpacity: clampNumber(settings.brandMarkOpacity, 40, 100, 72),
    showBanner: settings.showBanner === true,
    bannerUrl: getString(settings.bannerUrl, ''),
    bannerAlt: getString(settings.bannerAlt, `${brandName} banner`),
    bannerDisplay: getBannerDisplay(settings.bannerDisplay),
    bannerHeight: clampNumber(settings.bannerHeight, 120, 520, 220),
    bannerRadius: clampNumber(settings.bannerRadius, 0, 32, 16),
    bannerFit: getBannerFit(settings.bannerFit),
    bannerFocalX: clampNumber(settings.bannerFocalX, 0, 100, 50),
    bannerFocalY: clampNumber(settings.bannerFocalY, 0, 100, 50),
    bannerZoom: clampNumber(settings.bannerZoom, 1, 3, 1),
    bannerOverlayColor: getString(settings.bannerOverlayColor, '#000000'),
    bannerOverlayOpacity: clampNumber(settings.bannerOverlayOpacity, 0, 80, 16),
  }
}

export function getFormBackground(theme: FormTheme, settings: Record<string, unknown>) {
  const backgroundType = getFormBackgroundType(settings.backgroundType)

  if (backgroundType === 'gradient') {
    return `linear-gradient(135deg, ${theme.backgroundColor} 0%, ${theme.accentColor} 100%)`
  }

  if (backgroundType === 'image' || backgroundType === 'video') {
    return `radial-gradient(circle at 18% 14%, ${theme.accentColor}33, transparent 34%), radial-gradient(circle at 82% 18%, ${theme.buttonColor}24, transparent 32%), ${theme.backgroundColor}`
  }

  return theme.backgroundColor
}

export function getFormThemeVariables(theme: FormTheme): CSSProperties {
  const bodyFontFamily = getFormBodyFont(theme)
  const headingFontFamily = getFormHeadingFont(theme)
  const fontScale = theme.fontScale ?? 'comfortable'
  const lineHeight = theme.lineHeight ?? 1.55
  const sizes = typographySizes[fontScale]
  const readableTheme = getReadableThemeSurface(theme)

  return {
    '--form-bg': theme.backgroundColor,
    '--form-text': theme.textColor,
    '--form-answer': theme.answerColor,
    '--form-label': readableTheme.label,
    '--form-muted': readableTheme.muted,
    '--form-control-bg': readableTheme.controlBg,
    '--form-control-border': readableTheme.controlBorder,
    '--form-placeholder': readableTheme.placeholder,
    '--form-panel-bg': readableTheme.panelBg,
    '--form-panel-border': readableTheme.panelBorder,
    '--form-accent': theme.accentColor,
    '--form-accent-a11y': getReadableBackgroundColor(theme.accentColor, theme.buttonTextColor),
    '--form-button': theme.buttonColor,
    '--form-button-a11y': getReadableBackgroundColor(theme.buttonColor, theme.buttonTextColor),
    '--form-button-text': theme.buttonTextColor,
    '--form-radius': `${theme.radius}px`,
    '--form-max-width': `${theme.maxWidth}px`,
    '--form-font': bodyFontFamily,
    '--form-heading-font': headingFontFamily,
    '--form-body-font': bodyFontFamily,
    '--form-question-size': sizes.question,
    '--form-body-size': sizes.body,
    '--form-input-size': sizes.input,
    '--form-meta-size': sizes.meta,
    '--form-line-height': String(lineHeight),
  } as CSSProperties
}

function getReadableThemeSurface(theme: FormTheme) {
  const parsedBackground = parseHexColor(theme.backgroundColor)
  const isDark = parsedBackground !== null && getRelativeLuminance(parsedBackground) < 0.28

  if (isDark) {
    return {
      label: theme.textColor,
      muted: theme.answerColor,
      controlBg: '#1e293b',
      controlBorder: '#64748b',
      placeholder: '#94a3b8',
      panelBg: '#172033',
      panelBorder: '#334155',
    }
  }

  return {
    label: theme.answerColor,
    muted: theme.answerColor,
    controlBg: '#ffffff',
    controlBorder: '#cbd5e1',
    placeholder: '#64748b',
    panelBg: 'rgba(255,255,255,0.82)',
    panelBorder: 'rgba(203,213,225,0.82)',
  }
}

export function getFormLayoutVariables(settings: Record<string, unknown>): CSSProperties {
  const layout = getFormLayoutSettings(settings)
  const spacing = answerSpacingValues[layout.answerSpacing]

  return {
    '--form-question-align': layout.questionAlign,
    '--form-answer-gap': spacing.answerGap,
    '--form-question-block-gap': spacing.questionBlockGap,
    '--form-page-padding-block': spacing.pagePaddingBlock,
  } as CSSProperties
}

export function getFormPlayerStyle(
  theme: FormTheme,
  settings: Record<string, unknown>,
): CSSProperties {
  return {
    ...getFormThemeVariables(theme),
    ...getFormLayoutVariables(settings),
    background: getFormBackground(theme, settings),
    color: theme.textColor,
    fontFamily: getFormBodyFont(theme),
  }
}

export function getFormHeadingFont(theme: FormTheme) {
  return theme.headingFontFamily ?? theme.fontFamily
}

export function getFormBodyFont(theme: FormTheme) {
  return theme.bodyFontFamily ?? theme.fontFamily
}

function getQuestionAlignment(value: unknown): FormQuestionAlignment {
  if (value === 'center' || value === 'right') return value
  return 'left'
}

function getAnswerSpacing(value: unknown): FormAnswerSpacing {
  if (value === 'compact' || value === 'roomy') return value
  return 'comfortable'
}

function getNavigationLayout(value: unknown): FormNavigationLayout {
  if (value === 'split' || value === 'stacked') return value
  return 'inline'
}

function getLogoPosition(value: unknown): FormLogoPosition {
  if (value === 'top-center' || value === 'top-right') return value
  return 'top-left'
}

function getLogoShape(value: unknown): FormLogoShape {
  if (value === 'none' || value === 'circle') return value
  return 'rounded'
}

function getReadableBackgroundColor(background: string, foreground: string) {
  const parsedBackground = parseHexColor(background)
  const parsedForeground = parseHexColor(foreground)
  if (!parsedBackground || !parsedForeground) return background

  let candidate = parsedBackground
  for (let ratio = 0; ratio <= 0.5; ratio += 0.04) {
    candidate = mixRgb(parsedBackground, { r: 0, g: 0, b: 0 }, ratio)
    if (getContrastRatio(candidate, parsedForeground) >= 4.5) {
      return rgbToHex(candidate)
    }
  }

  return rgbToHex(candidate)
}

function parseHexColor(value: string) {
  const normalized = value.trim().replace(/^#/, '')
  const hex =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : normalized
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null

  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  }
}

function mixRgb(
  from: { r: number; g: number; b: number },
  to: { r: number; g: number; b: number },
  ratio: number,
) {
  return {
    r: Math.round(from.r * (1 - ratio) + to.r * ratio),
    g: Math.round(from.g * (1 - ratio) + to.g * ratio),
    b: Math.round(from.b * (1 - ratio) + to.b * ratio),
  }
}

function rgbToHex(color: { r: number; g: number; b: number }) {
  return `#${[color.r, color.g, color.b]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`
}

function getContrastRatio(
  first: { r: number; g: number; b: number },
  second: { r: number; g: number; b: number },
) {
  const firstLuminance = getRelativeLuminance(first)
  const secondLuminance = getRelativeLuminance(second)
  const lighter = Math.max(firstLuminance, secondLuminance)
  const darker = Math.min(firstLuminance, secondLuminance)
  return (lighter + 0.05) / (darker + 0.05)
}

function getRelativeLuminance(color: { r: number; g: number; b: number }) {
  const [r, g, b] = [color.r, color.g, color.b].map((channel) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function getBrandMarkPosition(value: unknown): FormBrandMarkPosition {
  if (value === 'bottom-left' || value === 'bottom-right') return value
  return 'bottom-center'
}

function getBrandMarkStyle(value: unknown): FormBrandMarkStyle {
  if (value === 'subtle' || value === 'solid') return value
  return 'pill'
}

function getBannerDisplay(value: unknown): FormBannerDisplay {
  if (value === 'questions' || value === 'all') return value
  return 'welcome'
}

function getBannerFit(value: unknown): FormBannerFit {
  if (value === 'contain') return value
  return 'cover'
}

function getString(value: unknown, fallback: string) {
  return typeof value === 'string' ? value : fallback
}

function clampNumber(value: unknown, min: number, max: number, fallback: number) {
  const numericValue = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numericValue)) return fallback
  return Math.min(max, Math.max(min, numericValue))
}

function getInitials(value: string) {
  const words = value.trim().split(/\s+/).filter(Boolean)

  if (words.length === 0) return 'CF'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
}
